import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faArrowLeft,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/UserCenter.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

const Recharge = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // 预设金额选项
  const amountOptions = [50, 100, 200, 500, 1000];

  // 获取用户信息
  useEffect(() => {
    let storedUser = localStorage.getItem('userData');
    
    // 如果userData不存在，尝试使用user键
    if (!storedUser) {
      storedUser = localStorage.getItem('user');
      // 如果找到了user信息，同时存储到userData中以保持一致性
      if (storedUser) {
        localStorage.setItem('userData', storedUser);
      }
    }
    
    if (!storedUser) {
      navigate('/login');
      return;
    }
    
    try {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
      
      // 确保yue字段存在
      if (!parsedUser.yue) {
        parsedUser.yue = '0.00';
        localStorage.setItem('userData', JSON.stringify(parsedUser));
        localStorage.setItem('user', JSON.stringify(parsedUser));
      }
    } catch (err) {
      console.error('Error parsing user data', err);
      navigate('/login');
    }
  }, [navigate]);

  // 处理金额输入
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // 只允许输入数字和小数点，且小数点后最多两位
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      setAmount(value);
    }
  };

  // 选择预设金额
  const selectAmount = (value) => {
    if (value > 0) {
      setAmount(value.toString());
    }
  };

  // 处理支付方式选择
  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  // 提交充值请求
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 验证金额
    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      setError(t('recharge.error'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t('payment.pleaseLoginFirst'));
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      const response = await axios.post('/yonghu/recharge', {
        userId: userData.id,
        rechargeAmount: amountValue.toFixed(2), // 确保发送两位小数
        paymentMethod
      });

      if (response.data.code === 0) {
        setSuccess(true);
        // 更新本地存储的用户信息中的余额
        const newBalance = (parseFloat(userData.yue || 0) + amountValue).toFixed(2);
        const updatedUser = {
          ...userData,
          yue: newBalance
        };
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUserData(updatedUser);
        
        toast.success(t('recharge.rechargeSuccess'));
        // 3秒后返回用户详情页
        setTimeout(() => {
          navigate(`/users/${userData.id}`);
        }, 3000);
      } else {
        setError(response.data.msg || t('recharge.rechargeFailed'));
        toast.error(response.data.msg || t('recharge.rechargeFailed'));
      }
    } catch (err) {
      console.error('充值请求出错：', err);
      setError(t('payment.networkError'));
      toast.error(t('payment.networkError'));
    } finally {
      setLoading(false);
    }
  };

  // 返回个人中心
  const goBack = () => {
    // 返回到用户详情页而不是用户中心
    if (userData && userData.id) {
      navigate(`/users/${userData.id}`);
    } else {
      navigate('/user-center');
    }
  };

  if (!userData) {
    return <div className="loading-message">{t('recharge.loading')}</div>;
  }

  return (
    <div className="user-center-container">
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="page-header">
        <button className="btn-link" onClick={goBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t('giftCards.backToCenter')}
        </button>
        <h1>
          <FontAwesomeIcon icon={faMoneyBillWave} /> {t('recharge.title')}
        </h1>
      </div>

      {success ? (
        <div className="success-message">
          <FontAwesomeIcon icon={faCheck} size="3x" />
          <h2>{t('recharge.rechargeSuccess')}</h2>
          <p>{t('recharge.rechargeSuccessDesc').replace('3秒', '')}</p>
          <button className="btn btn-primary" onClick={goBack}>{t('payment.backBtn')}</button>
        </div>
      ) : (
        <div className="recharge-form-container">
          <div className="account-info">
            <h3>{t('userMgmt.account')}: {userData.zhanghao || userData.username}</h3>
            <p>{t('payment.paymentAmount')}: ¥{userData.yue || '0.00'}</p>
          </div>

          <form className="recharge-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('recharge.title')}</label>
              <div className="amount-options">
                {amountOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`amount-option ${amount === option.toString() ? 'selected' : ''}`}
                    onClick={() => selectAmount(option)}
                  >
                    ¥{option}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>{t('payment.paymentAmount')}</label>
              <div className="custom-amount-input">
                <span className="currency-symbol">¥</span>
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder={t('payment.paymentAmount')}
                  pattern="^\\d*\\.?\\d{0,2}$"
                  title={t('payment.paymentAmount')}
                />
              </div>
              {error && <div className="error-message"><FontAwesomeIcon icon={faTimes} /> {error}</div>}
            </div>

            <div className="form-group">
              <label>{t('payment.paymentMethod')}</label>
              <div className="payment-methods">
                <div className="payment-method">
                  <input
                    type="radio"
                    id="alipay"
                    name="paymentMethod"
                    value="alipay"
                    checked={paymentMethod === 'alipay'}
                    onChange={handlePaymentMethodChange}
                  />
                  <label htmlFor="alipay">{t('payment.alipay')}</label>
                </div>
                <div className="payment-method">
                  <input
                    type="radio"
                    id="wechat"
                    name="paymentMethod"
                    value="wechat"
                    checked={paymentMethod === 'wechat'}
                    onChange={handlePaymentMethodChange}
                  />
                  <label htmlFor="wechat">{t('payment.wechat')}</label>
                </div>
                <div className="payment-method">
                  <input
                    type="radio"
                    id="creditCard"
                    name="paymentMethod"
                    value="creditCard"
                    checked={paymentMethod === 'creditCard'}
                    onChange={handlePaymentMethodChange}
                  />
                  <label htmlFor="creditCard">Card</label>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !amount}
              >
                {loading ? t('payment.processing') : t('recharge.title')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={goBack}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Recharge;
